import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
// import useMediaQuery from '@mui/material/useMediaQuery';

// import { check_subdomain } from '../../lib/server_helper';
import { fetch_all, fetch_one } from "../../../lib/v31lib";
import { loginAtom } from '../../../lib/auth';
import { currentPracticeAtom } from '../../../lib/practice';

import AddBoxIcon from '@mui/icons-material/AddBox';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import VideoCallIcon from '@mui/icons-material/VideoCall';

import {
  Avatar,
  Box,
  Divider,
  Grid,
} from '@mui/material';

import {
  Button,
  Fab,
  IconButton,
  Page,
  Typography
} from '../../../components/v2/styled';
import { KnowledgeAddAttachment } from '../../../components/v2/dialogs/Knowledge/AddAttachment';
import { KnowledgeAddLesson } from '../../../components/v2/dialogs/Knowledge/AddLesson';
import { KnowledgeAddSubject } from '../../../components/v2/dialogs/Knowledge/AddSubject';
import { StyledKnowledgeSubject } from '../../../components/v2/styled/knowledge/Subject';
import { StyledKnowledgeQuiz } from '../../../components/v2/styled/knowledge/Quiz';
import { StyledKnowledgeLesson } from '../../../components/v2/styled/knowledge/Lesson';
import { StyledKnowledgeLessonContent } from '../../../components/v2/styled/knowledge/LessonContent';
import { SnackDestroyed } from '../../../components/v2/snacks/Destroyed';

import { KnowledgeAttachment } from '../../../models/KnowledgeAttachment';
import { KnowledgeCourse } from '../../../models/KnowledgeCourse';
import { KnowledgeSubject } from '../../../models/KnowledgeSubject';
import { Questionnaire } from '../../../models/Questionnaire';

// import HelpersKnowledge from '../../../actions/helpers/knowledge';
import HelpersLanguage from '../../../actions/helpers/language';

// const { DateTime } = require("luxon");

type Props = {}

export const CoachKnowledgeBuilder: FunctionComponent<Props> = ({}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);
  const params = useParams();
  // const queryClient = useQueryClient();

  const [objectId, setObjectId] = useState<string | undefined>(params.id);

  const [subjectEditOpen, setSubjectEditOpen] = useState<boolean>(false);
  const [attachmentOpen, setAttachmentOpen] = useState<boolean>(false);
  const [currentAttachment, setCurrentAttachment] = useState<KnowledgeAttachment>();
  const [editingId, setEditingId] = useState<number>();
  const [activeSubject, setActiveSubject] = useState<KnowledgeSubject>();
  const [lessonEditOpen, setLessonEditOpen] = useState<boolean>(false);
  const [editingLessonId, setEditingLessonId] = useState<number>();
  const [destroyedOpen, setDestroyedOpen] = useState<boolean>(false);

  const {
    data: object,
    isLoading: objectLoading,
    isError: objectError,
    isSuccess: objectSuccess,
    refetch: objectRefetch,
  } = useQuery({
    queryKey: ["knowledge_course", (login?.practice_id || currentPractice?.id), objectId],
    queryFn: () =>
      fetch_one<KnowledgeCourse>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'knowledge_course',
          id: objectId,
          fields: [
            'id', 'name', 'teaser', 'body', 'extra_data', 'access_data', 'mollie_data', 'get_item',
            'enabled', 'is_hidden', 'is_frontpage', 'is_open_order', 'is_emails', 'is_approvable',
            'review_id', 'quiz_id', 'videos'
          ],
          sub_fields: {
            quiz: ['id', 'extra_data', 'name']
          },
          practice_id: (login?.practice_id || currentPractice?.id)
        },
        login
      ),
    enabled: !!(login?.practice_id || currentPractice?.id) && parseInt(objectId || '', 10) > 0,
  });

  const {
    data: subjects,
    // isLoading: objectLoading,
    // isError: objectError,
    // isSuccess: objectSuccess,
    refetch: subjectsRefetch,
  } = useQuery({
    queryKey: ["knowledge_subjects", object?.id, (login?.practice_id || currentPractice?.id), objectId],
    queryFn: () =>
      fetch_all<KnowledgeSubject>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'knowledge_subject',
          fields: [
            'id', 'name', 'body', 'weight', 'quiz_id', 'knowledge_lessons', 'extra_data'
          ],
          sub_fields: {
            quiz: ['id', 'extra_data', 'name'],
            knowledge_lessons: [
              'id', 'extra_data', 'name', 'body', 'full_body', 'weight', 'real_duration', 
              'public_duration', 'quiz_id', 'video_file_name', 'videos', 'attachments', 'access_data'
            ]
          },
          practice_id: (login?.practice_id || currentPractice?.id),
          order: 'weight ASC, name ASC',
          filter: {
            search: '',
            advanced: {
              knowledge_course_id: object?.id
            }
          }
        },
        login
      ),
    enabled: !!(login?.practice_id || currentPractice?.id) && !!object?.id,
  });

  const {
    data: questionnaires,
    // isLoading: objectsLoading,
    // isError: objectsError,
    isSuccess: questionnairesSuccess,
    refetch: questionnairesRefetch,
  } = useQuery({
    queryKey: ["coach", "questionnaires", (login?.practice_id || currentPractice?.id)],
    queryFn: () =>
      fetch_all<Questionnaire>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'questionnaire',
          fields: ['id', 'name', 'content_type', 'extra_data'],
          order: 'name ASC',
          per_page: 200,
          page: 0,
          filter: {
            search: '',
            advanced: {
              only_for_knowledge: 1,
              practice_id: (login?.practice_id || currentPractice?.id)
            }
          },
          practice_id: (login?.practice_id || currentPractice?.id)
        },
        login
      ),
    enabled: !!(login?.practice_id || currentPractice?.id),
  });











  const mutationDestroy = useMutation({
    mutationFn: (pars: {
      formData: any;
    }) => {
      return fetch_one<KnowledgeSubject>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/remove`,
        pars.formData,
        login
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      return true;
    },
    onError: (data, variables, context) => {
      return true;
    },
    onSuccess: (data, variables, context) => {
      setDestroyedOpen(true);
      subjectsRefetch();
    },
  });












  useEffect(() => {
    if(!lessonEditOpen) setActiveSubject(undefined);
  }, [lessonEditOpen]);
  
  return <Page
    title={t("coach.menu.knowledge_builder")}
    titleCrumbs={[
      {
        title: t("coach.menu.knowledge_center"),
        path: "/knowledge_courses"
      }
    ]}
    sx={{paddingBottom: 10}}
  >
    <Grid container spacing={1}>
      {(subjects || []).map((subject, i) => <>
        <Grid item xs={12}>
          {!!object && <StyledKnowledgeSubject
            knowledgeCourse={object}
            knowledgeSubject={subject}
            i={i}
            setEditingId={setEditingId}
            setSubjectEditOpen={setSubjectEditOpen}
            mutationDestroy={mutationDestroy}
          />}
        </Grid>
        {(subject.knowledge_lessons || []).map((lesson, ii) => <>
          <Grid item xs={12} sx={{paddingLeft: "48px !important"}}>
            {!!object && <StyledKnowledgeLesson
              knowledgeCourse={object}
              knowledgeSubject={subject}
              knowledgeLesson={lesson}
              i={ii}
              setLessonEditingId={setEditingLessonId}
              setLessonEditOpen={setLessonEditOpen}
              setActiveSubject={setActiveSubject}
              mutationDestroy={mutationDestroy}
            />}
          </Grid>
          <Grid item xs={12} sx={{paddingLeft: "96px !important"}}>
            {!!object && <StyledKnowledgeLessonContent
              knowledgeCourse={object}
              knowledgeSubject={subject}
              knowledgeLesson={lesson}
              i={ii}
              setLessonEditingId={setEditingLessonId}
              setLessonEditOpen={setLessonEditOpen}
              setActiveSubject={setActiveSubject}
              mutationDestroy={mutationDestroy}
              setCurrentAttachment={setCurrentAttachment}
              setAttachmentOpen={setAttachmentOpen}
              questionnaire={(questionnaires || []).filter(fff => fff.id === lesson?.quiz_id)[0]}
            />}
          </Grid>
          {/* <Grid item xs={12} sx={{paddingLeft: "96px !important"}}>
            {!!lesson?.quiz_id && <StyledKnowledgeQuiz
              questionnaire={(questionnaires || []).filter(fff => fff.id === lesson?.quiz_id)[0]}
            />}
          </Grid> */}
          <Grid item xs={12}>
            <Box sx={{height: '10px'}}></Box>
          </Grid>
        </>)}
        <Grid item xs={12} sx={{paddingLeft: "48px !important"}}>
          {!!subject?.quiz_id && <StyledKnowledgeQuiz
            questionnaire={(questionnaires || []).filter(fff => fff.id === subject?.quiz_id)[0]}
            isSubject
          />}
        </Grid>
        <Grid item xs={12} sx={{paddingLeft: "48px !important"}}>
          <Box sx={{
            padding: 2,
            border: '1px solid transparent',
            borderColor: theme.palette.primary.main,
            borderRadius: 1,
            position: 'relative',
            paddingLeft: 10,
            cursor: 'pointer',
            display: 'inline-block',
            "&:hover": {
              backgroundColor: theme.palette.primary.main,
              "& > p": {
                color: 'white'
              },
              "& > .MuiAvatar-root": {
                backgroundColor: 'white'
              },
              "& > .MuiAvatar-root svg": {
                color: theme.palette.primary.main
              }
            }
          }} onClick={() => {
            setActiveSubject(subject);
            setLessonEditOpen(true);
          }}>
            <Avatar variant="square" sx={{position: 'absolute', top: 8, left: 8, backgroundColor: theme.palette.primary.main}}>
              <LibraryAddIcon />
            </Avatar>
            <Typography sx={{display: 'inline-block'}}>{t("coach.knowledge_builder.add_lesson")}</Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{height: '50px'}}></Box>
        </Grid>
      </>)}
      <Grid item xs={12}>
        <StyledKnowledgeQuiz
          questionnaire={(questionnaires || []).filter(fff => fff.id === object?.quiz_id)[0]}
          isCourse
          onClick={() => {
            navigate(`/knowledge_course/${object?.id}?r=builder`);
          }}
        />
      </Grid>

      {/* <Grid item xs={12}>
        <Box sx={{
          // padding: 2,
          border: '1px solid transparent',
          borderColor: theme.palette.primary.main,
          borderRadius: 1,
          position: 'relative',
          // paddingLeft: 10,
          // cursor: 'pointer',
          display: 'inline-block',
          // "&:hover": {
          //   backgroundColor: theme.palette.primary.main,
          //   "& > p": {
          //     color: 'white'
          //   },
          //   "& > .MuiAvatar-root": {
          //     backgroundColor: 'white'
          //   },
          //   "& > .MuiAvatar-root svg": {
          //     color: theme.palette.primary.main
          //   }
          // }
        }} onClick={() => {
          // setCurrentAttachment({
          //   knowledge_course_id: object?.id,
          //   knowledge_course: object
          // });
          // setAttachmentOpen(true);
        }}>
          {(object?.access_data?.availability || [1]).map(mmm => HelpersLanguage.mapping_to_iso(mmm)).map((locale, i) => <>
            <Box sx={{
              padding: 1,
              // border: '1px solid transparent',
              // borderColor: theme.palette.primary.main,
              // borderRadius: 1,
              position: 'relative',
              paddingLeft: 10,
              paddingRight: 12,
              cursor: 'pointer',
              "&:hover": {
                backgroundColor: theme.palette.primary.main,
                "& > p": {
                  color: 'white'
                },
                "& > h5": {
                  color: 'white'
                },
                "& > .MuiAvatar-root": {
                  backgroundColor: 'white',
                  color: theme.palette.primary.main,
                },
                "& > .MuiAvatar-root svg": {
                  color: theme.palette.primary.main
                }
              },
              ...(i === 0 ? {
                borderRadius: "18px 18px 0px 0px"
              } : {}),
              ...(i === (HelpersLanguage.available_locales().length - 1) ? {
                borderRadius: "0px 0px 18px 18px"
              } : {})
            }} onClick={() => {
              setCurrentAttachment({
                knowledge_course_id: object?.id,
                knowledge_course: object,
                is_featured: true,
                locale: locale,
                get_item: ((object?.videos || {})[locale] || {}).get_item,
                id: ((object?.videos || {})[locale] || {}).id
              });
              setAttachmentOpen(true);
            }}>
              <Avatar variant={true ? "circular" : "square"} sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                backgroundColor: !!((object?.videos || {})[locale] || {}).item_file_name ? theme.palette.primary.main : 'lightgrey',
                border: '1px solid white',
                zIndex: 1201
              }}>
                <OndemandVideoIcon />
              </Avatar>
              <Avatar variant={true ? "circular" : "square"} sx={{
                position: 'absolute',
                top: 8,
                left: 38,
                backgroundColor: !!((object?.videos || {})[locale] || {}).item_file_name ? theme.palette.primary.main : 'lightgrey',
                border: '1px solid white',
                zIndex: 1200
              }}>
                {locale}
              </Avatar>
              {!!((object?.videos || {})[locale] || {}).item_file_name && <Typography variant="h5" sx={{marginTop: 1, marginBottom: 1}}>{((object?.videos || {})[locale] || {}).item_file_name}</Typography>}
              {!((object?.videos || {})[locale] || {}).item_file_name && <Typography variant="body1" sx={{marginTop: 1, marginBottom: 1, fontStyle: 'italic', color: 'lightgrey'}}>{t("general.terms.no_file_present")}</Typography>}
            </Box>
          </>)}
        </Box>
      </Grid> */}

      <Grid item xs={12}>
        <Box sx={{
          padding: 2,
          border: '1px solid transparent',
          borderColor: theme.palette.primary.main,
          borderRadius: 1,
          position: 'relative',
          paddingLeft: 10,
          cursor: 'pointer',
          display: 'inline-block',
          "&:hover": {
            backgroundColor: theme.palette.primary.main,
            "& > p": {
              color: 'white'
            },
            "& > .MuiAvatar-root": {
              backgroundColor: 'white'
            },
            "& > .MuiAvatar-root svg": {
              color: theme.palette.primary.main
            }
          }
        }} onClick={() => {
          setSubjectEditOpen(true);
        }}>
          <Avatar variant="square" sx={{position: 'absolute', top: 8, left: 8, backgroundColor: theme.palette.primary.main}}>
            <AddBoxIcon />
          </Avatar>
          <Typography sx={{display: 'inline-block'}}>{t("coach.knowledge_builder.add_subject")}</Typography>
        </Box>
      </Grid>
    </Grid>




    {!!object && !!subjectEditOpen && <KnowledgeAddSubject
      knowledgeCourse={object}
      open={subjectEditOpen}
      setOpen={setSubjectEditOpen}
      refetchSubjects={subjectsRefetch}
      subjects={subjects}
      objectId={editingId}
      setObjectId={setEditingId}
    />}
    {!!object && !!activeSubject && !!lessonEditOpen && <KnowledgeAddLesson
      knowledgeCourse={object}
      knowledgeSubject={activeSubject}
      open={lessonEditOpen}
      setOpen={setLessonEditOpen}
      refetchSubjects={subjectsRefetch}
      objectId={editingLessonId}
      setObjectId={setEditingLessonId}
    />}
    {!!object && !!currentAttachment && <KnowledgeAddAttachment
      currentAttachment={currentAttachment}
      open={attachmentOpen}
      setOpen={setAttachmentOpen}
      refetchSubjects={subjectsRefetch}
    />}
   
    <SnackDestroyed open={destroyedOpen} setOpen={setDestroyedOpen} />
  </Page>;
}
